/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import type { SearchResultItem } from '../api/search/search.types';
import { CardImage } from './parts/CardImage';
import { CardInventoryTag } from './parts/CardInventoryTag';
import { CardLocation } from './parts/CardLocation';
import { CardLinkHeading } from './parts/CardLinkHeading';
import { useCardInteraction } from './hooks/useCardInteraction';

import styles from './CompactSearchResultCard.module.scss';

export interface CompactSearchResultCardProps extends React.HTMLAttributes<HTMLDivElement> {
    item: SearchResultItem;
    onHover?: (item: SearchResultItem) => void;
    onHoverLeave?: (item: SearchResultItem) => void;
    gaTrackingId?: string;
}

export function CompactSearchResultCard({
    item,
    onHover,
    onHoverLeave,
    gaTrackingId,
    ...rest
}: CompactSearchResultCardProps) {
    const headingId = React.useId();
    const { onHoverOrFocus, onHoverLeaveOrBlur } = useCardInteraction({
        item,
        onHover,
        onHoverLeave,
    });

    // Bail out if no item is provided
    if (!item) return null;

    return (
        <div
            className={styles.compactSearchResultCard}
            onMouseEnter={onHoverOrFocus}
            onMouseLeave={onHoverLeaveOrBlur}
            onFocus={onHoverOrFocus}
            onBlur={onHoverLeaveOrBlur}
            aria-labelledby={headingId}
            {...rest}
        >
            <div className={styles.inner}>
                <div className={styles.imageWrap}>
                    <CardImage item={item} className={styles.image} />
                </div>

                <div className={styles.contentWrap}>
                    <div>
                        <CardInventoryTag
                            item={item}
                            hasText={false}
                            pillClassName={styles.inventoryPill}
                        />

                        <div>
                            <CardLinkHeading
                                item={item}
                                id={headingId}
                                className={styles.heading}
                                gaTrackingId={gaTrackingId}
                            />

                            <CardLocation
                                parent={item.parent_name}
                                distance={item.distance}
                                city={item.city}
                                state={item.state_code}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
